import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Modal, Spin, Switch, Tag, message } from "antd";
import {
  BellOutlined,
  CopyOutlined,
  ReloadOutlined,
  SendOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  getProspectSession,
  listLiveAgents,
  listProspectSessions,
  liveAgentMessage,
  liveEndHandoff,
  liveEndHandoffBeacon,
  liveReleaseHandoff,
  liveTakeover,
  liveTransferHandoff,
  liveTyping,
  openLiveSessionStream,
} from "./prospectService";
import styles from "./style.module.scss";

const FILTERS = ["all", "waiting", "in_progress", "ended", "mine"];
const LIST_POLL_MS = 5000;
const DETAIL_POLL_MS = 4000;
const ALERTS_STORAGE_KEY = "sp_live_alerts_v1";
const IDLE_WARN_MS = 12 * 60 * 1000;
const IDLE_END_MS = 15 * 60 * 1000;
const BASE_DOC_TITLE = typeof document !== "undefined" ? document.title : "";

function lastActivityMs(detail) {
  const msgs = detail?.messages || [];
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    const ts = Date.parse(msgs[i]?.ts || "");
    if (Number.isFinite(ts)) return ts;
  }
  const taken = Date.parse(detail?.live_handoff?.taken_over_at || "");
  return Number.isFinite(taken) ? taken : 0;
}
function mapSession(row) {
  const updatedAt = row.updated_at ? Date.parse(row.updated_at) : 0;
  const live =
    row.live_handoff && typeof row.live_handoff === "object"
      ? row.live_handoff
      : null;
  return {
    id: row.id,
    title: row.title || "",
    messageCount: row.message_count || 0,
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : 0,
    ip: row.ip || "",
    liveHandoff: live,
    leadEmail: row.lead?.email || "",
    pageUrl: row.page_url || "",
  };
}

function handoffStatus(live, updatedAt, now) {
  const status = live?.status;
  if (status === "taken_over") return "in_progress";
  if (status === "requested") return "waiting";
  if (status === "ended") return "ended";
  if (!updatedAt) return "available";
  const age = now - updatedAt;
  if (age <= 5 * 60 * 1000) return "waiting";
  return "available";
}

function formatTime(ts, locale) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      day: "numeric",
      month: "short",
    });
  } catch (_) {
    return "";
  }
}

function parseIsoMs(raw) {
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
}

function formatWaitLabel(requestedAt, now, t) {
  const start = parseIsoMs(requestedAt);
  if (!start) return "";
  const mins = Math.floor(Math.max(0, now - start) / 60000);
  if (mins < 1) return t("sonic_prospect.live_wait_just_now");
  return t("sonic_prospect.live_wait_minutes", { count: mins });
}

function readAlertsPref() {
  try {
    return window.localStorage.getItem(ALERTS_STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function writeAlertsPref(on) {
  try {
    window.localStorage.setItem(ALERTS_STORAGE_KEY, on ? "1" : "0");
  } catch (_) {
    /* ignore */
  }
}

function readSeenCounts(botId) {
  if (!botId) return {};
  try {
    const raw = window.sessionStorage.getItem(`sp_live_seen_${botId}`);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeSeenCounts(botId, counts) {
  if (!botId) return;
  try {
    window.sessionStorage.setItem(
      `sp_live_seen_${botId}`,
      JSON.stringify(counts),
    );
  } catch (_) {
    /* ignore */
  }
}

function playAlertBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.start(now);
    osc.stop(now + 0.25);
    osc.onended = () => {
      try {
        ctx.close();
      } catch (_) {
        /* ignore */
      }
    };
  } catch (_) {
    /* ignore */
  }
}

/** Match trailing `/query` for canned reply slash menu. */
function slashQuery(draft) {
  const m = String(draft || "").match(/(?:^|\s)\/([^\s/]*)$/);
  if (!m) return null;
  return m[1].toLowerCase();
}

function insertCannedBody(draft, body) {
  const text = String(draft || "");
  if (/\/[^\s/]*$/.test(text)) {
    return text.replace(/(^|\s)\/[^\s/]*$/, `$1${body}`);
  }
  return text ? `${text.trimEnd()} ${body}` : body;
}

function deriveSignals(messages, handoff) {
  const blob = (messages || [])
    .map((m) => String(m.content || ""))
    .join(" ")
    .toLowerCase();
  const pricing = /pric|seat|plan|cost|quote|ücret|fiyat|paket|koltuk/.test(blob);
  const lead = /@|email|e-posta|phone|telefon|\+90|\+1/.test(blob);
  const highIntent = pricing || /buy|purchase|demo|satın|almak|teklif/.test(blob);
  const friction = pricing
    ? "price_uncertainty"
    : /confus|anlamad|unclear|kararsız/.test(blob)
    ? "confusion"
    : "none";
  return {
    intent: pricing ? "Pricing" : highIntent ? "Purchase" : "General",
    purchaseIntent: highIntent ? "High" : "Medium",
    friction,
    pricingMatched: pricing,
    leadHint: lead,
    rule:
      pricing && handoff?.triggers?.pricingQuestion
        ? "Pricing question → Sales"
        : highIntent && handoff?.triggers?.highPurchaseIntent
        ? "High purchase intent → Sales"
        : handoff?.enabled
        ? "Handoff enabled"
        : "—",
  };
}

/**
 * Sales Handoff live console — polls sessions and talks to live handoff API.
 */
const BotHandoffLiveConsole = ({ bot, onBack, userId = "", embedded = false }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("tr") ? "tr-TR" : "en-US";
  const brandColor = bot?.appearance?.brandColor || "#7c3aed";
  const agentName = t("sonic_prospect.live_agent_you");

  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [now, setNow] = useState(Date.now());
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [alertsOn, setAlertsOn] = useState(() => readAlertsPref());
  const [seenCounts, setSeenCounts] = useState(() => readSeenCounts(bot?.id));
  const [slashIndex, setSlashIndex] = useState(0);
  const [showNewChip, setShowNewChip] = useState(false);
  const [threadQuery, setThreadQuery] = useState("");
  const [liveAgents, setLiveAgents] = useState([]);
  const [transferTo, setTransferTo] = useState("");

  const selectedRef = useRef(null);
  selectedRef.current = selectedId;
  const prevSnapshotRef = useRef(null);
  const alertsOnRef = useRef(alertsOn);
  alertsOnRef.current = alertsOn;
  const notifyReadyRef = useRef(false);
  const bubblesRef = useRef(null);
  const stickBottomRef = useRef(true);
  const filteredRef = useRef([]);
  const endHandoffRef = useRef(null);
  const isTakenOverRef = useRef(false);
  const isOwnerRef = useRef(false);
  const typingTimerRef = useRef(null);
  const typingSentRef = useRef(false);

  const markSeen = useCallback(
    (sessionId, count) => {
      if (!sessionId || !bot?.id) return;
      setSeenCounts((prev) => {
        const next = { ...prev, [sessionId]: count };
        writeSeenCounts(bot.id, next);
        return next;
      });
    },
    [bot?.id],
  );

  const fireAlert = useCallback(
    (title, body) => {
      playAlertBeep();
      if (!alertsOnRef.current) return;
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      try {
        const note = new Notification(title, { body, silent: true });
        void note;
      } catch (_) {
        /* ignore */
      }
    },
    [],
  );

  const detectAlerts = useCallback(
    (mapped) => {
      const snapshot = {};
      mapped.forEach((s) => {
        snapshot[s.id] = {
          status: s.liveHandoff?.status || "",
          messageCount: s.messageCount || 0,
        };
      });
      const prev = prevSnapshotRef.current;
      prevSnapshotRef.current = snapshot;
      if (!prev || !notifyReadyRef.current) {
        notifyReadyRef.current = true;
        return;
      }
      let newWaiting = false;
      let newVisitorMsg = false;
      Object.keys(snapshot).forEach((id) => {
        const cur = snapshot[id];
        const old = prev[id];
        if (cur.status === "requested" && (!old || old.status !== "requested")) {
          newWaiting = true;
        }
        if (
          cur.status === "taken_over" &&
          old &&
          cur.messageCount > (old.messageCount || 0) &&
          id !== selectedRef.current
        ) {
          newVisitorMsg = true;
        }
      });
      if (newWaiting) {
        fireAlert(
          t("sonic_prospect.live_notif_title"),
          t("sonic_prospect.live_notif_waiting"),
        );
      } else if (newVisitorMsg) {
        fireAlert(
          t("sonic_prospect.live_notif_title"),
          t("sonic_prospect.live_notif_message"),
        );
      }
    },
    [fireAlert, t],
  );

  const loadList = useCallback(
    async ({ quiet } = {}) => {
      if (!bot?.id) return;
      if (!quiet) setLoading(true);
      try {
        const rows = await listProspectSessions(bot.id);
        const mapped = rows.map(mapSession);
        if (quiet) detectAlerts(mapped);
        else {
          prevSnapshotRef.current = Object.fromEntries(
            mapped.map((s) => [
              s.id,
              {
                status: s.liveHandoff?.status || "",
                messageCount: s.messageCount || 0,
              },
            ]),
          );
          notifyReadyRef.current = true;
        }
        setSessions(mapped);
        setSelectedId((prev) => {
          if (prev && mapped.some((s) => s.id === prev)) return prev;
          return mapped[0]?.id || null;
        });
        setNow(Date.now());
      } catch (_) {
        if (!quiet) {
          setSessions([]);
          setSelectedId(null);
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [bot?.id, detectAlerts],
  );

  const loadDetail = useCallback(
    async (sessionId, { quiet } = {}) => {
      if (!bot?.id || !sessionId) {
        setDetail(null);
        return;
      }
      if (!quiet) setDetailLoading(true);
      try {
        const session = await getProspectSession(bot.id, sessionId);
        if (selectedRef.current === sessionId) {
          setDetail(session);
          const count = Array.isArray(session?.messages)
            ? session.messages.length
            : session?.message_count || 0;
          markSeen(sessionId, count);
        }
      } catch (_) {
        if (selectedRef.current === sessionId && !quiet) setDetail(null);
      } finally {
        if (!quiet && selectedRef.current === sessionId) setDetailLoading(false);
      }
    },
    [bot?.id, markSeen],
  );

  useEffect(() => {
    setSeenCounts(readSeenCounts(bot?.id));
    prevSnapshotRef.current = null;
    notifyReadyRef.current = false;
  }, [bot?.id]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (!bot?.id) return undefined;
    const listTick = () => {
      loadList({ quiet: true });
      setNow(Date.now());
    };
    const listId = window.setInterval(listTick, LIST_POLL_MS);
    return () => window.clearInterval(listId);
  }, [bot?.id, loadList]);

  useEffect(() => {
    if (!bot?.id || !selectedId) return undefined;
    let pollId = null;
    const startPollFallback = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        if (selectedRef.current) {
          loadDetail(selectedRef.current, { quiet: true });
        }
        setNow(Date.now());
      }, DETAIL_POLL_MS);
    };
    const stopStream = openLiveSessionStream(bot.id, selectedId, {
      onEvent: (name, data) => {
        if (name !== "snapshot") return;
        if (selectedRef.current !== selectedId) return;
        setDetail((prev) => ({
          ...(prev || {}),
          ...data,
          messages: Array.isArray(data.messages)
            ? data.messages
            : prev?.messages || [],
        }));
        const count = Array.isArray(data.messages)
          ? data.messages.length
          : data.message_count || 0;
        markSeen(selectedId, count);
        setNow(Date.now());
      },
      onError: () => {
        startPollFallback();
      },
    });
    return () => {
      stopStream();
      if (pollId) window.clearInterval(pollId);
    };
  }, [bot?.id, selectedId, loadDetail, markSeen]);

  const enriched = useMemo(() => {
    return sessions.map((s) => {
      const status = handoffStatus(s.liveHandoff, s.updatedAt, now);
      const seen = seenCounts[s.id] ?? 0;
      const unread =
        s.id !== selectedId &&
        (status === "waiting" ||
          (status === "in_progress" && s.messageCount > seen));
      const waitLabel =
        status === "waiting"
          ? formatWaitLabel(s.liveHandoff?.requested_at, now, t)
          : "";
      const ownerId = String(s.liveHandoff?.agent_id || "");
      return {
        ...s,
        status,
        mine:
          s.liveHandoff?.status === "taken_over" &&
          !!userId &&
          ownerId === String(userId),
        unread,
        waitLabel,
        ownerName: s.liveHandoff?.agent_name || "",
      };
    });
  }, [sessions, now, seenCounts, selectedId, t, userId]);

  const waitingCount = useMemo(
    () => enriched.filter((s) => s.status === "waiting").length,
    [enriched],
  );

  useEffect(() => {
    const base = bot?.name
      ? `${bot.name} · ${t("sonic_prospect.live_title")}`
      : t("sonic_prospect.live_title");
    document.title =
      waitingCount > 0 ? `(${waitingCount}) ${base}` : base;
    return () => {
      document.title = BASE_DOC_TITLE;
    };
  }, [waitingCount, bot?.name, t]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter((s) => {
      if (filter === "waiting" && s.status !== "waiting") return false;
      if (filter === "in_progress" && s.status !== "in_progress") return false;
      if (filter === "ended" && s.status !== "ended") return false;
      if (filter === "mine" && !s.mine) return false;
      if (q && !(s.title || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [enriched, filter, search]);
  filteredRef.current = filtered;

  const cannedReplies = useMemo(() => {
    const list = bot?.handoff?.cannedReplies;
    return Array.isArray(list) ? list : [];
  }, [bot?.handoff?.cannedReplies]);

  const slashQ = slashQuery(draft);
  const slashMatches = useMemo(() => {
    if (slashQ === null) return [];
    return cannedReplies.filter((r) => {
      const hay = `${r.title} ${r.id}`.toLowerCase();
      return !slashQ || hay.includes(slashQ);
    });
  }, [cannedReplies, slashQ]);

  useEffect(() => {
    setSlashIndex(0);
  }, [slashQ, slashMatches.length]);

  const messages = detail?.messages || [];
  const signals = useMemo(
    () => deriveSignals(messages, bot?.handoff),
    [messages, bot?.handoff],
  );
  const liveHandoff = detail?.live_handoff || null;
  const isTakenOver = liveHandoff?.status === "taken_over";
  const ownerId = String(liveHandoff?.agent_id || "");
  const isOwner =
    isTakenOver && (!ownerId || (!!userId && ownerId === String(userId)));
  const isClaimedByOther = isTakenOver && !!ownerId && !isOwner;
  isTakenOverRef.current = isTakenOver;
  isOwnerRef.current = isOwner;

  useEffect(() => {
    if (!isOwner || !bot?.id || !selectedId) {
      if (typingSentRef.current && bot?.id && selectedId) {
        liveTyping(bot.id, selectedId, false).catch(() => {});
        typingSentRef.current = false;
      }
      return undefined;
    }
    if (!draft.trim()) {
      if (typingSentRef.current) {
        liveTyping(bot.id, selectedId, false).catch(() => {});
        typingSentRef.current = false;
      }
      return undefined;
    }
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    typingTimerRef.current = window.setTimeout(() => {
      liveTyping(bot.id, selectedId, true)
        .then(() => {
          typingSentRef.current = true;
        })
        .catch(() => {});
    }, 450);
    return () => {
      if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    };
  }, [draft, isOwner, bot?.id, selectedId]);
  const takenOverSessionRef = useRef(null);
  takenOverSessionRef.current =
    isOwner && selectedId && bot?.id
      ? { botId: bot.id, sessionId: selectedId }
      : null;

  const idleAgeMs = isTakenOver ? Math.max(0, now - lastActivityMs(detail)) : 0;
  const showIdleWarn =
    isOwner && idleAgeMs >= IDLE_WARN_MS && idleAgeMs < IDLE_END_MS;
  const idleMinsLeft = Math.max(
    1,
    Math.ceil((IDLE_END_MS - idleAgeMs) / 60000),
  );

  useEffect(() => {
    if (!bot?.id) return undefined;
    let cancelled = false;
    listLiveAgents(bot.id)
      .then((rows) => {
        if (!cancelled) setLiveAgents(rows);
      })
      .catch(() => {
        if (!cancelled) setLiveAgents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [bot?.id]);

  const scrollBubblesToEnd = useCallback(() => {
    const el = bubblesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickBottomRef.current = true;
    setShowNewChip(false);
  }, []);

  const onBubblesScroll = () => {
    const el = bubblesRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickBottomRef.current = dist < 48;
    if (stickBottomRef.current) setShowNewChip(false);
  };

  useEffect(() => {
    if (!messages.length) return;
    if (stickBottomRef.current) {
      scrollBubblesToEnd();
    } else {
      setShowNewChip(true);
    }
  }, [messages.length, selectedId, scrollBubblesToEnd]);

  useEffect(() => {
    stickBottomRef.current = true;
    setShowNewChip(false);
    setThreadQuery("");
  }, [selectedId]);

  const threadQ = threadQuery.trim().toLowerCase();
  const visibleMessages = useMemo(() => {
    if (!threadQ) return messages;
    return messages.filter((m) =>
      String(m?.content || "")
        .toLowerCase()
        .includes(threadQ),
    );
  }, [messages, threadQ]);

  const highlightText = (text) => {
    const raw = String(text || "");
    if (!threadQ) return raw;
    const lower = raw.toLowerCase();
    const parts = [];
    let start = 0;
    let idx = lower.indexOf(threadQ, start);
    while (idx !== -1) {
      if (idx > start) parts.push(raw.slice(start, idx));
      parts.push(
        <mark key={`${idx}-${start}`} className={styles.liveSearchMark}>
          {raw.slice(idx, idx + threadQ.length)}
        </mark>,
      );
      start = idx + threadQ.length;
      idx = lower.indexOf(threadQ, start);
    }
    if (start < raw.length) parts.push(raw.slice(start));
    return parts.length ? parts : raw;
  };

  const applyCanned = useCallback((reply) => {
    if (!reply?.body) return;
    setDraft((prev) => insertCannedBody(prev, reply.body));
  }, []);

  const copyLastVisitor = useCallback(async () => {
    const last = [...messages].reverse().find((m) => m?.role === "user");
    if (!last?.content) {
      message.info(t("sonic_prospect.live_copy_visitor_empty"));
      return;
    }
    try {
      await navigator.clipboard.writeText(String(last.content));
      message.success(t("sonic_prospect.live_copy_visitor_ok"));
    } catch (_) {
      message.error(t("sonic_prospect.msg_save_error"));
    }
  }, [messages, t]);

  useEffect(() => {
    const endOnLeave = () => {
      const cur = takenOverSessionRef.current;
      if (!cur) return;
      liveEndHandoffBeacon(cur.botId, cur.sessionId, {
        reason: "agent_disconnect",
        system_text: "Sales left the chat. The assistant is back.",
      });
    };
    window.addEventListener("beforeunload", endOnLeave);
    return () => {
      window.removeEventListener("beforeunload", endOnLeave);
      endOnLeave();
    };
  }, []);

  const onAlertsToggle = async (checked) => {
    if (checked && typeof Notification !== "undefined") {
      try {
        if (Notification.permission === "default") {
          await Notification.requestPermission();
        }
      } catch (_) {
        /* ignore */
      }
    }
    setAlertsOn(checked);
    writeAlertsPref(checked);
    if (checked) {
      message.success(t("sonic_prospect.live_alerts_on"));
    }
  };

  const takeOver = async () => {
    if (!selectedId || !bot?.id || busy) return;
    setBusy(true);
    try {
      const res = await liveTakeover(bot.id, selectedId, {
        greeting: t("sonic_prospect.live_agent_greeting"),
        system_text: t("sonic_prospect.live_system_handoff", {
          reason: signals.rule,
        }),
        agent_name: agentName,
      });
      if (res?.session) setDetail(res.session);
      await loadList({ quiet: true });
      setFilter("in_progress");
      message.success(t("sonic_prospect.live_takeover_ok"));
    } catch (err) {
      const code = String(err?.message || "");
      if (code.includes("HANDOFF_CLAIMED")) {
        message.warning(t("sonic_prospect.live_claimed_other"));
        await loadList({ quiet: true });
        if (selectedId) await loadDetail(selectedId, { quiet: true });
      } else {
        message.error(t("sonic_prospect.msg_save_error"));
      }
    } finally {
      setBusy(false);
    }
  };

  const releaseHandoff = async () => {
    if (!selectedId || !bot?.id || busy || !isOwner) return;
    setBusy(true);
    try {
      const res = await liveReleaseHandoff(bot.id, selectedId);
      takenOverSessionRef.current = null;
      if (res?.session) setDetail(res.session);
      setDraft("");
      setTransferTo("");
      await loadList({ quiet: true });
      message.info(t("sonic_prospect.live_release_ok"));
    } catch (_) {
      message.error(t("sonic_prospect.msg_save_error"));
    } finally {
      setBusy(false);
    }
  };

  const transferHandoff = async () => {
    if (!selectedId || !bot?.id || busy || !isOwner || !transferTo) return;
    setBusy(true);
    try {
      const res = await liveTransferHandoff(bot.id, selectedId, transferTo);
      takenOverSessionRef.current = null;
      if (res?.session) setDetail(res.session);
      setDraft("");
      setTransferTo("");
      await loadList({ quiet: true });
      message.success(t("sonic_prospect.live_transfer_ok"));
    } catch (err) {
      const code = String(err?.message || "");
      if (code.includes("INVALID_TARGET")) {
        message.warning(t("sonic_prospect.live_transfer_invalid"));
      } else {
        message.error(t("sonic_prospect.msg_save_error"));
      }
    } finally {
      setBusy(false);
    }
  };

  const endHandoff = async () => {
    if (!selectedId || !bot?.id || busy) return;
    setBusy(true);
    try {
      const res = await liveEndHandoff(bot.id, selectedId, {
        system_text: t("sonic_prospect.live_system_ended"),
        reason: "agent_ended",
      });
      // Prevent unmount beacon from double-ending
      takenOverSessionRef.current = null;
      if (res?.session) setDetail(res.session);
      setDraft("");
      await loadList({ quiet: true });
      message.info(t("sonic_prospect.live_end_ok"));
    } catch (_) {
      message.error(t("sonic_prospect.msg_save_error"));
    } finally {
      setBusy(false);
    }
  };
  endHandoffRef.current = endHandoff;

  const confirmEndHandoff = useCallback(() => {
    if (!isTakenOverRef.current || busy) return;
    Modal.confirm({
      title: t("sonic_prospect.live_end_confirm_title"),
      content: t("sonic_prospect.live_end_confirm_body"),
      okText: t("sonic_prospect.live_btn_end"),
      okButtonProps: { danger: true },
      cancelText: t("sonic_prospect.btn_back"),
      onOk: () => endHandoffRef.current?.(),
    });
  }, [busy, t]);

  const slashMatchesRef = useRef([]);
  slashMatchesRef.current = slashMatches;

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const inField =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        e.target?.isContentEditable;
      if (e.key === "Escape") {
        if (slashMatchesRef.current.length > 0) {
          e.preventDefault();
          setDraft((prev) => prev.replace(/\/[^\s/]*$/, "").trimEnd());
          return;
        }
        if (isOwnerRef.current) {
          e.preventDefault();
          confirmEndHandoff();
        }
        return;
      }
      if (inField) return;
      if (e.key !== "j" && e.key !== "k" && e.key !== "J" && e.key !== "K") return;
      const list = filteredRef.current || [];
      if (!list.length) return;
      e.preventDefault();
      const cur = selectedRef.current;
      const idx = Math.max(
        0,
        list.findIndex((s) => s.id === cur),
      );
      const nextIdx =
        e.key === "j" || e.key === "J"
          ? Math.min(list.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      setSelectedId(list[nextIdx].id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmEndHandoff]);

  const sendAgent = async () => {
    const text = draft.trim();
    if (!text || !selectedId || !bot?.id || busy) return;
    if (!isOwner) {
      message.warning(
        isClaimedByOther
          ? t("sonic_prospect.live_claimed_other")
          : t("sonic_prospect.live_takeover_first"),
      );
      return;
    }
    setBusy(true);
    try {
      const res = await liveAgentMessage(bot.id, selectedId, text, {
        agent_name: agentName,
      });
      if (res?.session) setDetail(res.session);
      setDraft("");
      if (bot?.id && selectedId) {
        liveTyping(bot.id, selectedId, false).catch(() => {});
        typingSentRef.current = false;
      }
      await loadList({ quiet: true });
      stickBottomRef.current = true;
    } catch (err) {
      if (String(err?.message || "").includes("HANDOFF_NOT_ACTIVE")) {
        message.warning(t("sonic_prospect.live_takeover_first"));
      } else if (String(err?.message || "").includes("HANDOFF_NOT_OWNER")) {
        message.warning(t("sonic_prospect.live_claimed_other"));
      } else {
        message.error(t("sonic_prospect.msg_save_error"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.livePage}>
      <header className={styles.pageHeader}>
        <div>
          {embedded ? null : (
          <Button type="link" className={styles.backLink} onClick={onBack}>
            {t("sonic_prospect.btn_back")}
          </Button>
          )}
          <h1>{t("sonic_prospect.live_title")}</h1>
          <p>
            {bot?.name || t("sonic_prospect.preview_bot_fallback")} ·{" "}
            {t("sonic_prospect.live_subtitle")}
          </p>
        </div>
        <div className={styles.headerActions}>
          <label className={styles.liveAlertsToggle}>
            <BellOutlined />
            <span>{t("sonic_prospect.live_alerts")}</span>
            <Switch
              size="small"
              checked={alertsOn}
              onChange={onAlertsToggle}
            />
          </label>
          <Button icon={<ReloadOutlined />} onClick={() => loadList()}>
            {t("sonic_prospect.btn_refresh")}
          </Button>
        </div>
      </header>

      <div className={styles.liveLayout}>
        <aside className={styles.liveRail}>
          <div className={styles.liveRailHead}>
            <strong>{t("sonic_prospect.live_queue_title")}</strong>
            <span className={styles.liveRailCounts}>
              {waitingCount > 0 ? (
                <span className={styles.liveBadge} title={t("sonic_prospect.live_waiting_count", { count: waitingCount })}>
                  {waitingCount}
                </span>
              ) : null}
              <span className={styles.liveRailTotal}>{filtered.length}</span>
            </span>
          </div>
          <div className={styles.liveFilters}>
            {FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={
                  filter === key ? styles.liveFilterActive : styles.liveFilter
                }
                onClick={() => setFilter(key)}
              >
                {t(`sonic_prospect.live_filter_${key}`)}
              </button>
            ))}
          </div>
          <Input
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("sonic_prospect.live_search")}
          />
          {loading ? (
            <div className={styles.centered}>
              <Spin />
            </div>
          ) : filtered.length === 0 ? (
            <div className={styles.convEmpty}>
              <p>{t("sonic_prospect.live_queue_empty")}</p>
            </div>
          ) : (
            filtered.map((session) => (
              <button
                type="button"
                key={session.id}
                className={
                  session.id === selectedId
                    ? styles.liveSessionActive
                    : styles.liveSession
                }
                onClick={() => setSelectedId(session.id)}
              >
                <div className={styles.liveSessionTop}>
                  <span className={styles.liveSessionTitle}>
                    {session.unread ? (
                      <span className={styles.liveUnreadDot} aria-hidden />
                    ) : null}
                    {session.title || t("sonic_prospect.conversations_untitled")}
                  </span>
                  <span
                    className={`${styles.liveStatus} ${
                      styles[`liveStatus_${session.status}`] || ""
                    }`}
                  >
                    {t(`sonic_prospect.live_status_${session.status}`)}
                  </span>
                </div>
                <span className={styles.liveSessionMeta}>
                  {session.leadEmail ? (
                    <span className={styles.liveLeadMeta}>
                      {session.leadEmail}
                      {" · "}
                    </span>
                  ) : null}
                  {session.waitLabel ? (
                    <span className={styles.liveWaitMeta}>
                      {session.waitLabel}
                      {" · "}
                    </span>
                  ) : null}
                  {t("sonic_prospect.conversations_messages", {
                    count: session.messageCount,
                  })}
                  {session.updatedAt
                    ? ` · ${formatTime(session.updatedAt, locale)}`
                    : ""}
                </span>
              </button>
            ))
          )}
        </aside>

        <section className={styles.liveChat}>
          {!selectedId ? (
            <div className={styles.convEmpty}>
              <p>{t("sonic_prospect.live_pick")}</p>
            </div>
          ) : detailLoading && !detail ? (
            <div className={styles.centered}>
              <Spin />
            </div>
          ) : (
            <>
              <div className={styles.liveChatHead}>
                <div>
                  <strong>
                    {detail?.title || t("sonic_prospect.conversations_untitled")}
                  </strong>
                  <div className={styles.liveChatMeta}>
                    {detail?.ip
                      ? t("sonic_prospect.conversations_ip", { ip: detail.ip })
                      : t("sonic_prospect.conversations_ip_unknown")}
                    {liveHandoff?.status === "requested" &&
                    liveHandoff?.requested_at
                      ? ` · ${formatWaitLabel(liveHandoff.requested_at, now, t)}`
                      : ""}
                  </div>
                </div>
                <div className={styles.liveChatActions}>
                  {isOwner ? (
                    <>
                      {liveAgents.length > 0 ? (
                        <select
                          className={styles.liveReplySelect}
                          value={transferTo}
                          disabled={busy}
                          onChange={(e) => setTransferTo(e.target.value)}
                          aria-label={t("sonic_prospect.live_btn_transfer")}
                        >
                          <option value="">
                            {t("sonic_prospect.live_transfer_ph")}
                          </option>
                          {liveAgents.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name || a.email || a.id}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      {transferTo ? (
                        <Button loading={busy} onClick={transferHandoff}>
                          {t("sonic_prospect.live_btn_transfer")}
                        </Button>
                      ) : null}
                      <Button loading={busy} onClick={releaseHandoff}>
                        {t("sonic_prospect.live_btn_release")}
                      </Button>
                      <Button danger loading={busy} onClick={confirmEndHandoff}>
                        {t("sonic_prospect.live_btn_end")}
                      </Button>
                    </>
                  ) : isClaimedByOther ? (
                    <Tag color="orange">
                      {t("sonic_prospect.live_claimed_by", {
                        agent: liveHandoff?.agent_name || "Sales",
                      })}
                    </Tag>
                  ) : (
                    <Button
                      type="primary"
                      icon={<UserSwitchOutlined />}
                      loading={busy}
                      onClick={takeOver}
                    >
                      {t("sonic_prospect.live_btn_takeover")}
                    </Button>
                  )}
                </div>
              </div>

              {isOwner ? (
                <div className={styles.liveBanner}>
                  {t("sonic_prospect.live_banner_active", {
                    agent: liveHandoff?.agent_name || agentName,
                  })}
                </div>
              ) : isClaimedByOther ? (
                <div className={styles.liveBannerWarn}>
                  {t("sonic_prospect.live_banner_claimed", {
                    agent: liveHandoff?.agent_name || "Sales",
                  })}
                </div>
              ) : liveHandoff?.status === "requested" ? (
                <div className={styles.liveBanner}>
                  {t("sonic_prospect.live_banner_requested")}
                </div>
              ) : (
                <div className={styles.liveBannerMuted}>
                  {t("sonic_prospect.live_banner_idle")}
                </div>
              )}
              {showIdleWarn ? (
                <div className={styles.liveBannerWarn}>
                  {t("sonic_prospect.live_idle_warn", { count: idleMinsLeft })}
                </div>
              ) : null}
              <Input
                allowClear
                size="small"
                value={threadQuery}
                onChange={(e) => setThreadQuery(e.target.value)}
                placeholder={t("sonic_prospect.live_thread_search")}
                className={styles.liveThreadSearch}
              />

              <div className={styles.liveBubblesWrap}>
                <div
                  className={styles.liveBubbles}
                  ref={bubblesRef}
                  onScroll={onBubblesScroll}
                >
                {visibleMessages.length === 0 ? (
                  <div className={styles.convEmpty}>
                    <p>{t("sonic_prospect.live_thread_search_empty")}</p>
                  </div>
                ) : (
                visibleMessages.map((msg, i) => {
                  if (msg.role === "system") {
                    return (
                      <div key={`sys-${i}`} className={styles.liveSystem}>
                        {highlightText(msg.content)}
                      </div>
                    );
                  }
                  const isUser = msg.role === "user";
                  const isAgent = msg.role === "agent";
                  return (
                    <div
                      key={`${msg.ts || i}-${i}`}
                      className={
                        isUser
                          ? styles.convBubbleUser
                          : isAgent
                          ? styles.liveBubbleAgent
                          : styles.convBubbleBot
                      }
                      style={
                        !isUser && !isAgent
                          ? {
                              background: `${brandColor}18`,
                              borderColor: `${brandColor}44`,
                            }
                          : undefined
                      }
                    >
                      <span className={styles.convBubbleRole}>
                        {isUser
                          ? t("sonic_prospect.conversations_visitor")
                          : isAgent
                          ? msg.agent_name || agentName
                          : bot?.name || t("sonic_prospect.preview_bot_fallback")}
                        {msg.ts ? ` · ${formatTime(msg.ts, locale)}` : ""}
                      </span>
                      <p>{highlightText(msg.content)}</p>
                    </div>
                  );
                })
                )}
                </div>
                {showNewChip ? (
                  <button
                    type="button"
                    className={styles.liveNewChip}
                    onClick={scrollBubblesToEnd}
                  >
                    {t("sonic_prospect.live_new_messages")}
                  </button>
                ) : null}
              </div>

              <div className={styles.liveComposer}>
                <div className={styles.liveComposerTools}>
                  <select
                    className={styles.liveReplySelect}
                    disabled={!isOwner || !cannedReplies.length}
                    value=""
                    onChange={(e) => {
                      const id = e.target.value;
                      const reply = cannedReplies.find((r) => r.id === id);
                      if (reply) applyCanned(reply);
                      e.target.value = "";
                    }}
                    aria-label={t("sonic_prospect.live_replies")}
                  >
                    <option value="">
                      {t("sonic_prospect.live_replies")}
                    </option>
                    {cannedReplies.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.title}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={copyLastVisitor}
                  >
                    {t("sonic_prospect.live_copy_visitor")}
                  </Button>
                </div>
                <div className={styles.liveComposerRow}>
                  {slashMatches.length > 0 ? (
                    <div className={styles.liveSlashMenu} role="listbox">
                      {slashMatches.map((r, idx) => (
                        <button
                          type="button"
                          key={r.id}
                          className={
                            idx === slashIndex
                              ? styles.liveSlashItemActive
                              : styles.liveSlashItem
                          }
                          onMouseDown={(e) => {
                            e.preventDefault();
                            applyCanned(r);
                          }}
                        >
                          <strong>{r.title}</strong>
                          <span>{r.body.slice(0, 72)}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <Input.TextArea
                    rows={2}
                    value={draft}
                    disabled={!isOwner || busy}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={
                      isOwner
                        ? t("sonic_prospect.live_composer_ph")
                        : isClaimedByOther
                        ? t("sonic_prospect.live_claimed_other")
                        : t("sonic_prospect.live_takeover_first")
                    }
                    onPressEnter={(e) => {
                      if (e.metaKey || e.ctrlKey) {
                        e.preventDefault();
                        sendAgent();
                        return;
                      }
                      if (slashMatches.length > 0 && !e.shiftKey) {
                        e.preventDefault();
                        const pick =
                          slashMatches[
                            Math.min(slashIndex, slashMatches.length - 1)
                          ];
                        if (pick) applyCanned(pick);
                        return;
                      }
                      if (!e.shiftKey) {
                        e.preventDefault();
                        sendAgent();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (!slashMatches.length) return;
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setSlashIndex((i) =>
                          Math.min(slashMatches.length - 1, i + 1),
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setSlashIndex((i) => Math.max(0, i - 1));
                      }
                    }}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={busy}
                    disabled={!isOwner || !draft.trim()}
                    onClick={sendAgent}
                  >
                    {t("sonic_prospect.live_btn_send")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>

        <aside className={styles.liveSide}>
          <section className={styles.liveSideCard}>
            <h3>{t("sonic_prospect.live_visitor_title")}</h3>
            {detail?.lead?.email ? (
              <ul className={styles.liveSignalList}>
                <li>
                  <span>{t("sonic_prospect.live_visitor_email")}</span>
                  <strong>{detail.lead.email}</strong>
                </li>
                {detail.lead.segment ? (
                  <li>
                    <span>{t("sonic_prospect.live_visitor_segment")}</span>
                    <strong>{detail.lead.segment}</strong>
                  </li>
                ) : null}
                {detail.lead.sector ? (
                  <li>
                    <span>{t("sonic_prospect.live_visitor_sector")}</span>
                    <strong>{detail.lead.sector}</strong>
                  </li>
                ) : null}
                {detail.lead.customer_range ? (
                  <li>
                    <span>{t("sonic_prospect.live_visitor_range")}</span>
                    <strong>{detail.lead.customer_range}</strong>
                  </li>
                ) : null}
                {detail.lead.captured_at ? (
                  <li>
                    <span>{t("sonic_prospect.live_visitor_captured")}</span>
                    <strong>
                      {formatTime(Date.parse(detail.lead.captured_at), locale)}
                    </strong>
                  </li>
                ) : null}
              </ul>
            ) : (
              <p className={styles.handoffHint}>
                {t("sonic_prospect.live_visitor_empty")}
              </p>
            )}
            <p className={styles.handoffHint}>
              {detail?.ip
                ? t("sonic_prospect.conversations_ip", { ip: detail.ip })
                : t("sonic_prospect.conversations_ip_unknown")}
            </p>
          </section>

          <section className={styles.liveSideCard}>
            <h3>{t("sonic_prospect.live_page_title")}</h3>
            {detail?.page_url ? (
              <p className={styles.livePageLink}>
                <a href={detail.page_url} target="_blank" rel="noopener noreferrer">
                  {detail.page_url}
                </a>
              </p>
            ) : (
              <p className={styles.handoffHint}>
                {t("sonic_prospect.live_page_empty")}
              </p>
            )}
            {detail?.referrer ? (
              <p className={styles.livePageLink}>
                <span>{t("sonic_prospect.live_page_referrer")}</span>
                <a href={detail.referrer} target="_blank" rel="noopener noreferrer">
                  {detail.referrer}
                </a>
              </p>
            ) : null}
          </section>

          <section className={styles.liveSideCard}>
            <h3>{t("sonic_prospect.live_signals_title")}</h3>
            <p className={styles.handoffHint}>
              {t("sonic_prospect.live_signals_heuristic")}
            </p>
            <ul className={styles.liveSignalList}>
              <li>
                <span>{t("sonic_prospect.live_signal_intent")}</span>
                <strong>{signals.intent}</strong>
              </li>
              <li>
                <span>{t("sonic_prospect.live_signal_purchase")}</span>
                <strong>{signals.purchaseIntent}</strong>
              </li>
              <li>
                <span>{t("sonic_prospect.live_signal_friction")}</span>
                <strong>
                  {t(`sonic_prospect.live_friction_${signals.friction}`, {
                    defaultValue: signals.friction,
                  })}
                </strong>
              </li>
            </ul>
            <div className={styles.liveChipRow}>
              {signals.pricingMatched ? (
                <span className={styles.liveChip}>
                  {t("sonic_prospect.live_chip_pricing")}
                </span>
              ) : null}
              {signals.leadHint ? (
                <span className={styles.liveChip}>
                  {t("sonic_prospect.live_chip_lead_chat")}
                </span>
              ) : null}
              {detail?.lead?.email ? (
                <span className={styles.liveChip}>
                  {t("sonic_prospect.live_chip_lead_captured")}
                </span>
              ) : null}
            </div>
          </section>
          <section className={styles.liveSideCard}>
            <h3>{t("sonic_prospect.live_routing_title")}</h3>
            <p>{signals.rule}</p>
            <p className={styles.handoffHint}>
              {t("sonic_prospect.live_routing_dest", {
                dest: t(
                  bot?.handoff?.routing?.destination === "hubspot"
                    ? "sonic_prospect.handoff_dest_hubspot"
                    : bot?.handoff?.routing?.destination === "email"
                    ? "sonic_prospect.handoff_dest_email"
                    : "sonic_prospect.handoff_dest_sales",
                ),
              })}
            </p>
          </section>
          <section className={styles.liveSideCard}>
            <h3>{t("sonic_prospect.live_context_title")}</h3>
            <p>
              {t("sonic_prospect.live_context_messages", {
                count: messages.length,
              })}
            </p>
            <p>
              {bot?.handoff?.enabled
                ? t("sonic_prospect.live_handoff_on")
                : t("sonic_prospect.live_handoff_off")}
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default BotHandoffLiveConsole;
